import { FC, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ClearIcon from "@mui/icons-material/Clear";
import {
  AppBar,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
} from "@mui/material";
import github from "./img/github.svg";
import linkedin from "./img/linkedin.svg";
import email from "./img/email.png";
import styled from "styled-components/macro";
import MainDrawer from "./MainDrawer";
import { motion } from "framer-motion";

const StyledAppBar = styled(AppBar)<{ hide?: boolean }>`
  && {
    background: transparent;
    box-shadow: none;
    align-items: center;
    margin-top: 77vh;
    margin-right: 30px;
    position: fixed;
    @media screen and (max-width: 1000px) {
      position: unset;
      margin-top: 0vh;
      display: ${({ hide }) => (hide ? "none" : "")};
    }
    .MuiGrid-container {
      justify-content: center;
    }
    .MuiIconButton-root {
      background: #e6236d;
      border-radius: 0;
    }

    img:hover {
      transition: all 0.5s ease-in-out;
      opacity: 0.6;
    }

    .contact {
      background: ${({ theme }) => theme.contactBackground};
      border-radius: 50px;
      padding: 12px 10px;
      > div {
        display: flex;
        flex-direction: column;
      }
      @media screen and (max-width: 1000px) {
        display: none !important;
      }
    }
    .mobile-menu {
      @media screen and (min-width: 1000px) {
        display: none !important;
      }
    }
  }
`;

const StyledDrawer = styled(Drawer)<{ hide?: boolean }>`
  @media screen and (max-width: 1000px) {
    display: ${({ hide }) => (hide ? "none" : "")};
  }
  div.MuiPaper-root {
    width: 100%;
    background: #9294ff;
    color: white;
    overflow-x: hidden;
  }
  .MuiIconButton-root {
    margin: 20px;
    float: right;
  }
  ul {
    height: ${window.innerHeight}px;
    width: 100%;
    padding: 150px 0 0 80px;

    a {
      font-size: 30px;
      font-weight: bold;
      color: inherit;
      font-family: "Bebas Neue", cursive;
      :hover {
        text-decoration: underline;
        background: transparent;
      }
    }
    @media screen and (max-width: 1000px) {
      padding: 100px 0 0 20px;

      .MuiListItem-root {
        padding-bottom: 0;
      }
    }
  }
`;

const menuItems = [
  { name: "Portfolio", url: "portfolio" },
  { name: "Tech Stack", url: "techstack" },
  { name: "Experience", url: "experience" },
  { name: "Education", url: "education" },
  { name: "Projects", url: "projects" },
  { name: "Certifications", url: "certifications" },
];

const socialHover = {
  hover: {
    scale: [1, 1.3, 1.2, 1.25, 1], // subtle bounce
    rotate: [0, 10, -5, 5, 0], // little wiggly tilt
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};
const Menu: FC<{ id?: string }> = ({ id }) => {
  const location = useLocation();
  const isCategoryPage = location.pathname.includes("techstack/");
  const hideMenu = id === "main-menu" && isCategoryPage;
  const [open, setOpen] = useState<boolean>(false);
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setOpen(open);
    };

  return (
    <>
      <StyledAppBar hide={hideMenu} position="static">
        <Grid container style={{ justifyContent: "space-between" }}>
          <div />
          <Grid item className="mobile-menu">
            <IconButton
              onClick={toggleDrawer(true)}
              onKeyDown={toggleDrawer(true)}
              edge="start"
              color="inherit"
              aria-label="menu"
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          </Grid>

          {!isCategoryPage && (
            <motion.div
              initial={{ y: -100, opacity: 0 }}
              animate={{
                y: [-100, -20, 0, -10, 0], // subtle float then settle
                opacity: [0, 0.6, 1, 1, 1], // smooth fade-in
              }}
              transition={{
                duration: 2,
                ease: "easeOut", // smooth deceleration
                times: [0, 0.4, 0.7, 0.85, 1], // keyframe timing
              }}
              className="contact"
            >
              <div>
                <motion.a
                  href="https://github.com/xandrav"
                  target="_blank"
                  rel="noreferrer"
                  whileHover="hover"
                  variants={socialHover}
                  style={{ display: "inline-block", marginBottom: 10 }}
                >
                  <img src={github} width={30} height={30} alt="github" />
                </motion.a>
                <motion.a
                  href="https://www.linkedin.com/in/alexandra-veres95/"
                  target="_blank"
                  rel="noreferrer"
                  whileHover="hover"
                  variants={socialHover}
                  style={{ display: "inline-block", marginBottom: 10 }}
                >
                  <img src={linkedin} width={30} height={30} alt="linkedin" />
                </motion.a>
                <motion.a
                  href="mailto: veresalexandra95@gmail.com"
                  target="_blank"
                  rel="noreferrer"
                  whileHover="hover"
                  variants={socialHover}
                  style={{ display: "inline-block" }}
                >
                  <img src={email} width={27} height={27} alt="email" />
                </motion.a>
              </div>
            </motion.div>
          )}
        </Grid>
      </StyledAppBar>

      <MainDrawer hide={hideMenu} />

      <StyledDrawer
        hide={hideMenu}
        anchor={"right"}
        open={open}
        onClose={toggleDrawer(false)}
      >
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <IconButton
            onClick={toggleDrawer(false)}
            edge="start"
            color="inherit"
            aria-label="clear"
          >
            <ClearIcon fontSize="large" />
          </IconButton>
          <List>
            {menuItems.map(({ name, url }) => (
              <ListItem key={`menu-${name}`} component={Link} to={`/${url}`}>
                {name}
              </ListItem>
            ))}
          </List>
        </div>
      </StyledDrawer>
    </>
  );
};

export default Menu;
